import { z } from 'zod';

import { messageEventSchema, messageStatusSchema } from './enums.js';
import { s3UriSchema } from './media.js';
import { paginationQuerySchema } from './pagination.js';
import { phoneE164Schema } from './phone.js';

export const MAX_BATCH_SIZE = 500;
export const MAX_BODY_PARAMS = 10;

const mediaRefSchema = z.object({
  s3Uri: s3UriSchema,
  /** What the customer sees. Extension must match the file's MIME type. */
  filename: z.string().trim().min(1).max(240),
});

export const createMessageSchema = z
  .object({
    idempotencyKey: z.string().trim().min(1).max(200),
    to: phoneE164Schema,
    template: z.string().trim().min(1).max(512),
    language: z.string().trim().min(2).max(10).default('en'),
    bodyParams: z.array(z.string().max(1024)).max(MAX_BODY_PARAMS).default([]),
    document: mediaRefSchema.optional(),
    image: mediaRefSchema.optional(),
    correlationId: z.string().trim().max(200).optional(),
    metadata: z.record(z.unknown()).default({}),
  })
  .strict()
  .refine((v) => !(v.document && v.image), {
    message: 'Provide document or image, not both',
    path: ['document'],
  });
export type CreateMessageInput = z.input<typeof createMessageSchema>;
export type CreateMessage = z.infer<typeof createMessageSchema>;

export const createMessageResponseSchema = z.object({
  id: z.string().uuid(),
  status: messageStatusSchema,
  duplicate: z.boolean(),
});
export type CreateMessageResponse = z.infer<typeof createMessageResponseSchema>;

export const createBatchSchema = z
  .object({ messages: z.array(createMessageSchema).min(1).max(MAX_BATCH_SIZE) })
  .strict();
export type CreateBatchInput = z.input<typeof createBatchSchema>;

export const batchResultItemSchema = z.union([
  z.object({
    index: z.number().int(),
    id: z.string().uuid(),
    status: messageStatusSchema,
    duplicate: z.boolean(),
  }),
  z.object({
    index: z.number().int(),
    error: z.object({ code: z.string(), message: z.string() }),
  }),
]);
export const batchResponseSchema = z.object({ results: z.array(batchResultItemSchema) });
export type BatchResponse = z.infer<typeof batchResponseSchema>;

export const messageEventDtoSchema = z.object({
  id: z.string(),
  event: messageEventSchema,
  payload: z.record(z.unknown()).nullable(),
  occurredAt: z.string().datetime(),
});

export const messageDtoSchema = z.object({
  id: z.string().uuid(),
  idempotencyKey: z.string(),
  recipientPhone: z.string(),
  recipientName: z.string().nullable(),
  templateName: z.string(),
  templateLanguage: z.string(),
  bodyParams: z.array(z.string()),
  headerFilename: z.string().nullable(),
  status: messageStatusSchema,
  wamid: z.string().nullable(),
  errorCode: z.number().int().nullable(),
  errorTitle: z.string().nullable(),
  errorDetail: z.string().nullable(),
  permanentFailure: z.boolean(),
  attempts: z.number().int(),
  requestedBy: z.string(),
  correlationId: z.string().nullable(),
  metadata: z.record(z.unknown()),
  queuedAt: z.string().datetime(),
  sentAt: z.string().datetime().nullable(),
  deliveredAt: z.string().datetime().nullable(),
  readAt: z.string().datetime().nullable(),
  failedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type MessageDto = z.infer<typeof messageDtoSchema>;

export const messageDetailSchema = messageDtoSchema.extend({
  events: z.array(messageEventDtoSchema),
});
export type MessageDetail = z.infer<typeof messageDetailSchema>;

export const listMessagesQuerySchema = paginationQuerySchema.extend({
  status: z
    .union([messageStatusSchema, z.array(messageStatusSchema)])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v])),
  to: z.string().trim().optional(),
  template: z.string().trim().optional(),
  correlationId: z.string().trim().optional(),
  from: z.string().datetime().optional(),
  toDate: z.string().datetime().optional(),
});
export type ListMessagesQuery = z.infer<typeof listMessagesQuerySchema>;

export const retryMessageSchema = z.object({ force: z.boolean().default(false) }).strict();

export const bulkRetrySchema = z
  .object({ ids: z.array(z.string().uuid()).min(1).max(200), force: z.boolean().default(false) })
  .strict();
